import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { CreditCardOnFile } from '@liveauctioneers/hammer-ui-payments/checkout';
import { useEffect } from 'react';

import { CardSelection } from '@/components/PaymentMethodSelection/CardSelection';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchBidderCreditCardsIfNeeded } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.actions';
import {
    creditCardsAnyLoadingSelector,
    getLAPaymentCardsLoaded,
    laPaymentCardsForProviderSelector,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { getCatalogPaymentProvider } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { openAddCreditCardModal } from '@/redux/modules/modal';
import { CreditCard } from '@/types/CreditCard';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type Props = {
    active: boolean;
    catalogId: number;
    isSelected: boolean;
    onCardSelectionChange: (creditCard: CreditCard) => void;
    selectedCreditCard: CreditCard;
};

export const PaymentMethodCreditCardsSelection = ({
    active,
    catalogId,
    isSelected,
    onCardSelectionChange,
    selectedCreditCard,
}: Props) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const cardsLoading = useAppSelector(creditCardsAnyLoadingSelector);
    const creditCards = useAppSelector((state) => laPaymentCardsForProviderSelector(state, paymentProvider));

    const laPaymentCardLoaded = useAppSelector(getLAPaymentCardsLoaded);

    useEffect(() => {
        if (!laPaymentCardLoaded) {
            dispatch(fetchBidderCreditCardsIfNeeded());
        }
    }, [dispatch, laPaymentCardLoaded]);

    if (!isSelected) {
        return null;
    }

    if (!active) {
        return (
            selectedCreditCard && (
                <CreditCardOnFile
                    brand={selectedCreditCard.brand}
                    expiration={`${selectedCreditCard.month}/${selectedCreditCard.year}`}
                    lastFour={selectedCreditCard.last4}
                />
            )
        );
    }

    return (
        <div>
            {cardsLoading ? (
                <Loading />
            ) : (
                <>
                    <CardSelection
                        creditCards={creditCards}
                        selectCard={onCardSelectionChange}
                    />
                    <div className="mt-sm smMax:mb-lg">
                        <InlineButton
                            onClick={() =>
                                dispatch(
                                    openAddCreditCardModal(paymentProvider, {
                                        isCheckout: true,
                                    })
                                )
                            }
                        >
                            {formatMessage(['checkout', 'payment', 'addCreditCard'])}
                        </InlineButton>
                    </div>
                </>
            )}
        </div>
    );
};
